import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { CoverageStatistics, HotSpot } from "../../types/coverageStatistics";

interface CoverageStatisticsPanelProps {
  /** Coverage statistics to display */
  statistics: CoverageStatistics | null;
  /** Whether statistics are loading */
  isLoading?: boolean;
  /** Whether to show in compact mode */
  compact?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Callback when hovering a hot spot */
  onHotSpotHover?: (hotSpot: HotSpot | null) => void;
}

/**
 * Panel component displaying coverage statistics with expandable sections
 */
const CoverageStatisticsPanel: React.FC<CoverageStatisticsPanelProps> = ({
  statistics,
  isLoading = false,
  compact = false,
  className = "",
  onHotSpotHover,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(!compact);

  if (isLoading) {
    return (
      <div className={`bg-gray-50 rounded-lg p-3 ${className}`}>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600" />
          {t("statsNew.coverage.calculating")}
        </div>
      </div>
    );
  }

  if (!statistics) {
    return null;
  }

  const formatPercentage = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };

  const formatScore = (value: number): string => {
    return value.toFixed(2);
  };

  // Determine color based on coverage level
  const getCoverageColor = (coverage: number): string => {
    if (coverage >= 70) return "text-green-600";
    if (coverage >= 40) return "text-yellow-600";
    return "text-red-600";
  };

  // Determine description for entropy
  const getEntropyDescription = (entropy: number): string => {
    if (entropy >= 0.7) return t("statsNew.coverage.entropyExplorative");
    if (entropy >= 0.4) return t("statsNew.coverage.entropyBalanced");
    return t("statsNew.coverage.entropyFocused");
  };

  // Determine description for gini
  const getGiniDescription = (gini: number): string => {
    if (gini >= 0.7) return t("statsNew.coverage.giniConcentrated");
    if (gini >= 0.4) return t("statsNew.coverage.giniModerate");
    return t("statsNew.coverage.giniDistributed");
  };

  if (compact && !isExpanded) {
    // Compact collapsed view
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className={`bg-gray-50 hover:bg-gray-100 rounded-lg px-3 py-2 text-sm text-gray-600 flex items-center gap-2 transition-colors ${className}`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
        <span className={getCoverageColor(statistics.coveragePercentage)}>
          {formatPercentage(statistics.coveragePercentage)}
        </span>
        <span>{t("statsNew.coverage.coverage")}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 ml-auto"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
    );
  }

  return (
    <div className={`bg-gray-50 rounded-lg p-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          {t("statsNew.coverage.title")}
        </h4>
        {compact && (
          <button
            onClick={() => setIsExpanded(false)}
            className="p-1 hover:bg-gray-200 rounded transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 15l7-7 7 7"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Main stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
        {/* Coverage (with FOV) */}
        <div className="bg-white rounded-lg p-3 shadow-sm group relative">
          <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
            {t("statsNew.coverage.coverage")}
            <InfoIcon tooltip={t("statsNew.coverage.tooltipCoverage")} />
          </div>
          <div
            className={`text-xl font-semibold ${getCoverageColor(statistics.coveragePercentage)}`}
          >
            {formatPercentage(statistics.coveragePercentage)}
          </div>
          <div className="text-xs text-gray-400">{t("statsNew.coverage.ofScene")}</div>
        </div>

        {/* Exploration (area traversed by gaze movement) */}
        <div className="bg-white rounded-lg p-3 shadow-sm">
          <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
            {t("statsNew.coverage.exploration")}
            <InfoIcon tooltip={t("statsNew.coverage.tooltipExploration")} />
          </div>
          <div className="text-xl font-semibold text-blue-600">
            {formatPercentage(statistics.explorationPercentage)}
          </div>
          <div className="text-xs text-gray-400">{t("statsNew.coverage.areaTraversed")}</div>
        </div>

        {/* Dead Zones */}
        <div className="bg-white rounded-lg p-3 shadow-sm">
          <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
            {t("statsNew.coverage.deadZones")}
            <InfoIcon tooltip={t("statsNew.coverage.tooltipDeadZones")} />
          </div>
          <div className="text-xl font-semibold text-gray-700">
            {formatPercentage(statistics.deadZonePercentage)}
          </div>
          <div className="text-xs text-gray-400">{t("statsNew.coverage.notViewed")}</div>
        </div>

        {/* Entropy */}
        <div className="bg-white rounded-lg p-3 shadow-sm">
          <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
            {t("statsNew.coverage.entropy")}
            <InfoIcon tooltip={t("statsNew.coverage.tooltipEntropy")} />
          </div>
          <div className="text-xl font-semibold text-gray-700">
            {formatScore(statistics.entropy)}
          </div>
          <div className="text-xs text-gray-400">{getEntropyDescription(statistics.entropy)}</div>
        </div>

        {/* Gini */}
        <div className="bg-white rounded-lg p-3 shadow-sm">
          <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
            {t("statsNew.coverage.gini")}
            <InfoIcon tooltip={t("statsNew.coverage.tooltipGini")} />
          </div>
          <div className="text-xl font-semibold text-gray-700">
            {formatScore(statistics.giniCoefficient)}
          </div>
          <div className="text-xs text-gray-400">
            {getGiniDescription(statistics.giniCoefficient)}
          </div>
        </div>
      </div>

      {/* Hot Spots */}
      {statistics.hotSpots.length > 0 && (
        <div className="mt-4">
          <h5 className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3.5 w-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z"
              />
            </svg>
            {t("statsNew.coverage.hotSpots")} ({statistics.hotSpots.length})
            <InfoIcon tooltip={t("statsNew.coverage.tooltipHotSpots")} />
          </h5>
          <div className="flex flex-wrap gap-2">
            {statistics.hotSpots.map((hotSpot, index) => (
              <div
                key={hotSpot.id}
                className="bg-white rounded px-2 py-1 text-xs flex items-center gap-1.5 shadow-sm cursor-pointer hover:bg-blue-50 hover:ring-2 hover:ring-blue-300 transition-all"
                onMouseEnter={() => onHotSpotHover?.(hotSpot)}
                onMouseLeave={() => onHotSpotHover?.(null)}
              >
                <span
                  className="w-4 h-4 rounded-full flex items-center justify-center text-white font-medium"
                  style={{
                    backgroundColor: getHotSpotColor(index),
                  }}
                >
                  {index + 1}
                </span>
                <span className="text-gray-600">
                  {formatPercentage(hotSpot.attentionPercentage)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Processing time (debug) */}
      {process.env.NODE_ENV === "development" && (
        <div className="mt-3 text-xs text-gray-400">
          Calculated in {statistics.processingTime.toFixed(1)}ms
        </div>
      )}
    </div>
  );
};

/**
 * Get color for hot spot marker - uses warm colors to match "hot" concept
 */
function getHotSpotColor(_index: number): string {
  return "#ef4444"; // red-500 - matches heatmap hot zones
}

/**
 * Small info icon with tooltip on hover
 */
const InfoIcon: React.FC<{ tooltip: string }> = ({ tooltip }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="relative inline-block">
      <button
        type="button"
        className="text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onFocus={() => setShowTooltip(true)}
        onBlur={() => setShowTooltip(false)}
        aria-label="Information"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-3.5 w-3.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </button>
      {showTooltip && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-800 rounded-lg shadow-lg w-56 text-left">
          {tooltip}
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1">
            <div className="w-2 h-2 bg-gray-800 rotate-45" />
          </div>
        </div>
      )}
    </div>
  );
};

export default CoverageStatisticsPanel;
